import React from 'react'
import { TertiaryButton } from '@moonpig/launchpad-components'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql'
import { useLocaleText } from './ProductTile.locale'
import {
  getProductTileCtaLabel,
  getProductTileCtaType,
  ProductTileCtaEnum,
} from './getProductTileCta'

export type CtaClickProps = {
  href?: string
  onClick?: React.MouseEventHandler<HTMLElement>
}

type Props = React.ComponentPropsWithoutRef<typeof TertiaryButton> & {
  ctaProps: CtaClickProps
  department: DepartmentsEnum
  hasMultipleVariants: boolean
  isCustomisable: boolean
  isPrintedInCard: boolean
  iconOnly?: boolean
}

export const ProductTileCta = React.forwardRef<HTMLButtonElement, Props>(
  (props, ref) => {
    const {
      children,
      ctaProps,
      department,
      hasMultipleVariants,
      isCustomisable,
      isPrintedInCard,
      iconOnly = false,
      loading = false,
      ...rest
    } = props

    const localiseText = useLocaleText()

    const ctaParams = {
      department,
      hasMultipleVariants,
      isCustomisable,
      isPrintedInCard,
    }

    const ctaType = getProductTileCtaType(ctaParams)
    const ctaLabel = getProductTileCtaLabel(ctaParams, localiseText)
    const ctaText = ctaType === ProductTileCtaEnum.AddToBasket ? null : ctaLabel

    let buttonContent: React.ReactNode
    if (iconOnly && loading) {
      buttonContent = null
    } else if (iconOnly) {
      buttonContent = children
    } else {
      buttonContent = ctaText || children
    }

    const { className: restClassName, ...restWithoutClassName } = rest
    const className = [restClassName, loading && 'loading']
      .filter(Boolean)
      .join(' ')

    return (
      <TertiaryButton
        ref={ref}
        {...restWithoutClassName}
        {...ctaProps}
        loading={loading}
        aria-label={iconOnly ? ctaLabel : rest['aria-label']}
        className={className}
      >
        {buttonContent}
      </TertiaryButton>
    )
  },
)
